import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Fade,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Article as ArticleIcon,
  GitHub as GitHubIcon,
  Star as StarIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../../Assets/logo.png';

export const NavBar = (): React.ReactNode => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY >= 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { text: 'Home', path: '/', icon: <HomeIcon /> },
    { text: 'About', path: '/about', icon: <PersonIcon /> },
    { text: 'Engineering', path: '/engineering', icon: <BuildIcon /> },
    { text: 'Resume', path: '/resume', icon: <DescriptionIcon /> },
  ];

  const drawer = (
    <Box sx={{ width: 250 }}>
      <List>
        {navItems.map(item => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              color:
                location.pathname === item.path
                  ? 'primary.main'
                  : 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(138, 73, 168, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
        <ListItem
          component='a'
          href='https://islommashanlo.medium.com/'
          target='_blank'
          rel='noreferrer'
          sx={{
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'rgba(138, 73, 168, 0.1)',
            },
          }}
        >
          <ListItemIcon>
            <ArticleIcon />
          </ListItemIcon>
          <ListItemText primary='Blogs' />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position='fixed'
        elevation={scrolled ? 8 : 0}
        sx={{
          background: scrolled ? 'rgba(10, 25, 47, 0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease',
          zIndex: 1000,
        }}
      >
        <Container maxWidth='lg'>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Fade in={true} timeout={1000}>
              <Box
                component={Link}
                to='/'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <img
                  src={logo}
                  alt='logo'
                  style={{
                    height: '40px',
                    marginRight: '10px',
                  }}
                />
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                  }}
                >
                  Islom Mashanlo
                </Typography>
              </Box>
            </Fade>

            {isMobile ? (
              <IconButton
                color='inherit'
                aria-label='open drawer'
                edge='start'
                onClick={handleDrawerToggle}
                sx={{ color: 'primary.main' }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {navItems.map(item => (
                  <Button
                    key={item.text}
                    component={Link}
                    to={item.path}
                    startIcon={item.icon}
                    sx={{
                      color:
                        location.pathname === item.path
                          ? 'primary.main'
                          : 'text.primary',
                      '&:hover': {
                        backgroundColor: 'rgba(138, 73, 168, 0.1)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
                <Button
                  component='a'
                  href='https://islommashanlo.medium.com/'
                  target='_blank'
                  rel='noreferrer'
                  startIcon={<ArticleIcon />}
                  sx={{
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'rgba(138, 73, 168, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Blogs
                </Button>
                <Button
                  component='a'
                  href='https://github.com/islommashanlo?tab=repositories'
                  target='_blank'
                  rel='noreferrer'
                  variant='outlined'
                  startIcon={<GitHubIcon />}
                  endIcon={<StarIcon />}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  GitHub
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      <Drawer
        variant='temporary'
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Toolbar /> {/* Spacer for fixed AppBar */}
    </>
  );
};
